import { SocketAsFetch } from "@leagueplatform/socket-as-fetch";

export const GET_APP_INIT_DETAILS_MESSAGE_TYPE = "get_app_init_details";

// The SDK's transform pipeline does NOT fire for `get_app_init_details` —
// the wire payload arrives snake_case. Typings below describe the normalized
// view; consumers should read both shapes (via `readEligibilityOptions`
// below or `useJourneyNavEnabled`).
export type SegmentationInfo = {
  campaignId?: string;
  activityId?: string;
  lobId?: string;
  productBrandGrouping?: string;
};

// `groupNumber === "Active"` marks an active plan when `isActive` is absent.
export type EligibilityOption = {
  clientNumber: string;
  planName?: string;
  planType?: string;
  groupNumber: string;
  isActive?: boolean;
};

export type EligibilityInfo = {
  eligible: boolean;
  reasonCode: number;
  reasonCodeDescription: string;
};

export type BrandingInfo = {
  brandingLogo?: unknown;
  brandingLogoOpt?: unknown;
  brandingName?: string;
  brandingMessage?: string;
};

export type AppMessaging = {
  disclaimer?: string;
};

export type Qualifiers = {
  insuranceAdministeredByThirdParty?: boolean;
};

export type AppInitDetails = {
  eligible: boolean;
  eligibilityInfo?: EligibilityInfo;
  mfaEnrolled: boolean;
  brandingInfo: BrandingInfo;
  appMessaging?: AppMessaging;
  qualifiers?: Qualifiers;
  contentConfig: Record<string, string | null | undefined>;
  eligibilityOptions?: EligibilityOption[];
  segmentation?: Record<string, SegmentationInfo>;
};

export const getAppInitDetails = (): Promise<AppInitDetails> =>
  SocketAsFetch.fetch({
    message_type: GET_APP_INIT_DETAILS_MESSAGE_TYPE,
    info: { platform: "web" },
  });

// Every consumer of `eligibilityOptions` must go through
// `readEligibilityOptions` below so normalization happens in one place.
export type SnakeEligibilityOption = {
  client_number?: string;
  clientNumber?: string;
  plan_name?: string;
  planName?: string;
  plan_type?: string;
  planType?: string;
  group_number?: string;
  groupNumber?: string;
  is_active?: boolean;
  isActive?: boolean;
};

const normalizeOption = (raw: SnakeEligibilityOption): EligibilityOption => ({
  clientNumber: raw.clientNumber ?? raw.client_number ?? "",
  planName: raw.planName ?? raw.plan_name,
  planType: raw.planType ?? raw.plan_type,
  groupNumber: raw.groupNumber ?? raw.group_number ?? "",
  isActive: raw.isActive ?? raw.is_active,
});

export const readEligibilityOptions = (data: unknown): EligibilityOption[] => {
  const camel = (data as { eligibilityOptions?: SnakeEligibilityOption[] })
    ?.eligibilityOptions;
  if (Array.isArray(camel)) return camel.map(normalizeOption);
  const snake = (data as { eligibility_options?: SnakeEligibilityOption[] })
    ?.eligibility_options;
  if (Array.isArray(snake)) return snake.map(normalizeOption);
  return [];
};

// Mirrors iOS `PostAuthenticationCoordinator.validateUserEligibility()`
// (EMBLEM-7075): only `true` when the backend has explicitly signaled
// ineligibility via the nested `eligibility_info` block. Absence of the
// block, or a missing `eligible` field within it, is treated as
// eligible — Swift's `guard let ... else { return }` semantics. Reads
// both snake_case (actual wire shape, per module header) and camelCase
// so a future SDK transform doesn't silently break this check.
export const isMemberIneligible = (data: unknown): boolean => {
  const info =
    (data as { eligibilityInfo?: { eligible?: boolean } })?.eligibilityInfo ??
    (data as { eligibility_info?: { eligible?: boolean } })?.eligibility_info;
  return info?.eligible === false;
};
